import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import find from 'lodash/find';
import { IFooter, IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { createCETYear } from '~/ui/components/layouts/Footer/helper';
import {
  SignUpUpdates,
  TopFooterLinks,
  OtherHotelSites,
  BottomFooterLinks,
  AcceptedPayments,
} from '@Hotels-at-Home/hah-fe-core/dist/components';

import { parentHotelCompany, hotelBrandName } from '~/constants/siteConfig';
import { LG_WIDTH } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { useWindowSize } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { logger, getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils';

interface IProps {
  footer: IFooter;
  termsAndConditions: string;
  promotion: IGetPromotionResponse;
  fixedBottom?: boolean;
}

const Footer = ({ footer, termsAndConditions, promotion, fixedBottom }: IProps): ReactElement => {
  const getCETYear = useMemo(createCETYear, []);

  const { t } = useTranslation('', { keyPrefix: '' });

  const { width } = useWindowSize();

  const isLessThan960 = width && width <= LG_WIDTH;

  const topFooterWrapper = useRef<HTMLDivElement>(null);

  const bottomFooterWrapper = useRef<HTMLDivElement>(null);

  const [isEmployeeSite, setIsEmployeeSite] = useState(false);

  useEffect(() => {
    if (fixedBottom && topFooterWrapper.current && bottomFooterWrapper.current && !isLessThan960) {
      topFooterWrapper.current.style.marginBottom = `${bottomFooterWrapper.current.offsetHeight}px`;
    } else if (fixedBottom && topFooterWrapper.current && isLessThan960) {
      topFooterWrapper.current.style.marginBottom = '0px';
    }
  }, [width]);

  useEffect(() => {
    setIsEmployeeSite(getCookie('promo-tag') === 'employeesite');
  }, []);

  if (!footer) {
    logger(
      'Unable to get footer data from BO; please check if the "footer" block is correctly configured for this store view',
      { type: 'error' },
    );
  }

  const { bottomFooterLinks, customerSupportLinks, otherHotelSites } = footer || {};
  const privacyPolicy = find(bottomFooterLinks, { name: 'privacy-policy' }) || {};
  const chatSiteId = '2110591'; // works for every EU site
  const isMarriotSite = false; // enables Marriot cookies scripts
  const shouldShowTC = promotion?.promo_is_active && !(Number(promotion?.promo_days_left) < 0);

  return (
    <footer role="contentinfo" className={fixedBottom ? 'fixed-bottom' : ''}>
      <div className="footer-wrap-top" ref={topFooterWrapper}>
        {/* <Cookie /> */}
        {privacyPolicy && <SignUpUpdates privacyPolicy={privacyPolicy} variant="stacked" />}
      </div>
      <div className="footer-wrap-bottom" ref={bottomFooterWrapper}>
        <div className="footer-bottom" id="footerBottom">
          <div className="links copyright clearfix clear">
            <div className="footer-links-wrapper">
              <div className="footer-links-row">
                <span>{t('$local.needHelp')}</span>
                {customerSupportLinks && (
                  <TopFooterLinks
                    customerSupportLinks={customerSupportLinks}
                    chatSiteId={chatSiteId}
                  />
                )}
              </div>
              <div className="footer-links-row">
                <span>{t('$local.information')}</span>
                {bottomFooterLinks && (
                  <BottomFooterLinks
                    bottomFooterLinksData={bottomFooterLinks}
                    isMarriotSite={isMarriotSite}
                  />
                )}
              </div>
              <div className="footer-links-row payments">
                <span>{t('$local.acceptedPayments')}</span>
                <AcceptedPayments />
              </div>
            </div>
            {otherHotelSites && <OtherHotelSites otherHotelSites={otherHotelSites} />}
            <p className="footer__copyright" id="footer__copyright">
              {(parentHotelCompany as string) === 'Marriott'
                ? t('$marCommon.copyright', { year: `${getCETYear}` })
                : t('$local.copyright', {
                    year: `${getCETYear}`,
                    parentHotelCompany: parentHotelCompany.toUpperCase(),
                  })}
            </p>
            <div data-event-name="bloc_interact" data-bloc-name="footer logo">
              <p className="icon-hotel-logo" aria-label={`${hotelBrandName} logo`}>
                <span>{hotelBrandName}</span>
              </p>
            </div>
            {/* TODO: Below employee site logic is temporary. We should improve on this by getting the terms from the BO and perhaps moving some logic to the core. */}
            {!isEmployeeSite && shouldShowTC && (
              <p
                className="footer-terms-banner"
                dangerouslySetInnerHTML={{ __html: termsAndConditions }}
              />
            )}

            {isEmployeeSite && <p className="footer-terms-banner">{t('$local.employeeTerms')}</p>}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
